import { useMemo, useState } from "react";

import { api, useDatos } from "../api";
import { useAccion } from "../componentes/acciones";
import { Carga } from "../componentes/Carga";
import { Alert, Badge, DataTable, Input, Menu, MetricCard, Page, SegmentedControl, Switch, Text, TopBar, type DataTableColumn } from "../ds";
import { fechaHora, hace, numero } from "../formato";
import type { Zona } from "../tipos";
import s from "./Zonas.module.css";

export function Zonas() {
  const { datos, error, cargando, recargar, setDatos } = useDatos<Zona[]>("/api/zonas");
  const { ejecutar } = useAccion();
  const [tipo, setTipo] = useState<"todas" | "caba_barrio" | "gba_localidad">("todas");
  const [texto, setTexto] = useState("");

  const filas = useMemo(() => {
    const q = texto.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    return (datos ?? []).filter(
      (z) =>
        (tipo === "todas" || z.tipo === tipo) &&
        (!q || `${z.nombre} ${z.partido ?? ""}`.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").includes(q)),
    );
  }, [datos, tipo, texto]);

  const cambiar = async (z: Zona, cambios: Partial<Pick<Zona, "activa" | "prioridad">>) => {
    const antes = datos;
    setDatos((d) => d?.map((x) => (x.id === z.id ? { ...x, ...cambios } : x)));
    const r = await ejecutar(`zona-${z.id}`, () => api(`/api/zonas/${z.id}`, { metodo: "PATCH", cuerpo: cambios }), `${z.nombre}: guardado`);
    if (r === undefined) setDatos(antes);
  };

  const recorridas = (datos ?? []).filter((z) => z.ultimaCorrida).length;
  const requests = (datos ?? []).reduce((a, z) => a + z.requests, 0);
  const pendientesP1 = (datos ?? []).filter((z) => z.prioridad === 1 && z.activa && !z.ultimaCorrida).length;

  const columnas: DataTableColumn<Zona>[] = [
    {
      key: "nombre",
      header: "Zona",
      width: "24%",
      render: (z) => (
        <span className={s.nombre}>
          <Text as="span" size="sm" weight="semibold" tone={z.activa ? "strong" : "muted"}>
            {z.nombre}
          </Text>
          <Text as="span" size="xs" tone="muted">
            {z.partido ?? "CABA"} · radio {numero(z.radioM)} m
          </Text>
        </span>
      ),
    },
    {
      key: "prioridad",
      header: "Prioridad",
      width: "120px",
      render: (z) => (
        <Menu
          label={`Prioridad de ${z.nombre}`}
          trigger={
            <Badge as="button" tone={z.prioridad === 1 ? "brand" : "neutral"} iconAfter="chevron-down">
              Prioridad {z.prioridad}
            </Badge>
          }
          items={[1, 2, 3].map((p) => ({ label: `Prioridad ${p}`, selected: z.prioridad === p, onSelect: () => cambiar(z, { prioridad: p }) }))}
        />
      ),
    },
    {
      key: "activa",
      header: "Activa",
      width: "88px",
      render: (z) => <Switch aria-label={`${z.nombre} activa`} checked={z.activa} onChange={(e) => cambiar(z, { activa: e.target.checked })} />,
    },
    { key: "inmobiliarias", header: "Inmobiliarias", align: "right", mono: true, width: "112px", render: (z) => numero(z.inmobiliarias) },
    { key: "calificadas", header: "Calificadas", align: "right", mono: true, width: "104px", hideBelow: "md", render: (z) => numero(z.calificadas) },
    { key: "wa", header: "Con WhatsApp", align: "right", mono: true, width: "120px", hideBelow: "md", render: (z) => numero(z.conWhatsapp) },
    {
      key: "corrida",
      header: "Última búsqueda",
      width: "160px",
      render: (z) =>
        z.ultimaCorrida ? (
          <Text as="span" size="xs" title={fechaHora(z.ultimaCorrida)}>
            {hace(z.ultimaCorrida)}
          </Text>
        ) : (
          <Text as="span" size="xs" tone="muted">
            Nunca
          </Text>
        ),
    },
    { key: "requests", header: "Requests", align: "right", mono: true, width: "96px", hideBelow: "md", render: (z) => numero(z.requests) },
  ];

  return (
    <>
      <TopBar title="Zonas" subtitle="La grilla de búsqueda en Google Maps: barrios de CABA y localidades del conurbano." />
      <Page width="none">
        {!datos ? (
          <Carga error={error} onReintentar={recargar} filas={10} />
        ) : (
          <>
            <div className={s.metricas}>
              <MetricCard label="Zonas recorridas" value={`${recorridas} de ${datos.length}`} icon="map" period={pendientesP1 ? `Faltan ${pendientesP1} de prioridad 1` : "Prioridad 1 completa"} />
              <MetricCard label="Requests a Places" value={numero(requests)} icon="search" period="Desde el principio, todas las zonas" />
              <MetricCard label="Activas" value={numero(datos.filter((z) => z.activa).length)} icon="check-circle-2" period="Las inactivas no se vuelven a buscar" />
            </div>
            <Alert tone="info" title="La búsqueda corre desde la línea de comandos">
              Prioridad y activa deciden qué zonas recorre la próxima corrida. Para buscar: <code>pnpm cli places:buscar --prioridad 2</code> y después <code>enriquecer</code> y <code>puntuar</code>.
            </Alert>
            <div className={s.filtros}>
              <Input aria-label="Buscar zona" icon="search" type="search" placeholder="Buscar zona o partido" value={texto} onChange={(e) => setTexto(e.target.value)} className={s.busqueda} />
              <SegmentedControl
                label="Tipo"
                size="sm"
                value={tipo}
                onChange={setTipo}
                options={[
                  { value: "todas", label: "Todas" },
                  { value: "caba_barrio", label: "CABA" },
                  { value: "gba_localidad", label: "Conurbano" },
                ]}
              />
            </div>
            <DataTable columns={columnas} rows={filas} rowKey={(z) => z.id} loading={cargando} rowHighlight={(z) => (z.activa ? undefined : "muted")} minWidth="900px" empty="No hay zonas con ese nombre." />
          </>
        )}
      </Page>
    </>
  );
}
